import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatRelativeTimeET } from '../utils/formatters';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABELS = {
  property_address: 'Property Address',
  client_code: 'Client Code',
  client_product: 'Product Type',
  parcel: 'Parcel / APN',
  buyer_name: 'Buyer Name',
  seller_name: 'Seller Name',
  effective_date: 'Effective Date',
  notes: 'Order Notes',
  other: 'Other',
};

const EditRequestsPanel = ({ currentUser, onViewOrder, isCompact = false }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = statusFilter ? `?status_filter=${statusFilter}` : '';
      const res = await axios.get(`${API}/api/edit-requests${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data.requests || []);
      setPendingCount(res.data.pending_count || 0);
    } catch {
      toast.error('Failed to load edit requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
    const iv = setInterval(fetchRequests, 30000);
    return () => clearInterval(iv);
  }, [fetchRequests]);

  const handleAction = async (id, status, rejReason) => {
    setProcessing(id);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/edit-requests/${id}`, {
        status,
        rejection_reason: rejReason || null
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(status === 'approved' ? 'Request approved' : 'Request rejected');
      setRejectingId(null);
      setRejectionReason('');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(null);
    }
  };

  const badge = (s) => {
    const map = {
      pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      approved: 'bg-green-50 text-green-700 border-green-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      // legacy compat
      in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
    };
    const labels = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', in_progress: 'In Progress', completed: 'Completed' };
    return (
      <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium border ${map[s] || map.pending}`}>
        {labels[s] || s}
      </span>
    );
  };

  const canManage = ['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(currentUser?.role);
  if (!canManage) return null;

  // Compact widget
  if (isCompact) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="edit-requests-widget">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Edit Requests</h3>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">{pendingCount}</span>
          )}
        </div>
        <div className="p-3">
          {loading ? (
            <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
          ) : requests.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No pending requests</p>
          ) : (
            <div className="space-y-2">
              {requests.slice(0, 5).map(r => (
                <div key={r.id} className="p-2 bg-gray-50 rounded text-xs" data-testid={`edit-request-item-${r.id}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-800">{r.order_number}</span>
                    <span className="text-gray-400">{formatRelativeTimeET(r.created_at)}</span>
                  </div>
                  <p className="text-gray-500 mt-0.5 truncate">{FIELD_LABELS[r.field_name] || r.field_name}: {r.requested_value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="edit-requests-panel">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Edit Requests</h2>
          <p className="text-xs text-gray-500">Correction requests from Order Desk</p>
        </div>
        {pendingCount > 0 && (
          <span className="px-2.5 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">{pendingCount} pending</span>
        )}
      </div>

      {/* Tabs */}
      <div className="px-5 py-2 border-b border-gray-100 flex gap-1">
        {[
          { key: 'pending', label: 'Pending' },
          { key: 'approved', label: 'Approved' },
          { key: 'rejected', label: 'Rejected' },
          { key: '', label: 'All' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setStatusFilter(t.key)}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === t.key
                ? 'bg-gray-900 text-white'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
            data-testid={`filter-${t.key || 'all'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="divide-y divide-gray-100">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-400">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400">No requests</div>
        ) : (
          requests.map(r => (
            <div key={r.id} className="px-5 py-3 hover:bg-gray-50/50" data-testid={`edit-request-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      onClick={() => onViewOrder?.(r.order_id)}
                      className="text-sm font-semibold text-blue-600 hover:underline"
                      data-testid={`view-order-${r.order_number}`}
                    >
                      {r.order_number}
                    </button>
                    {badge(r.status)}
                    <span className="text-[11px] text-gray-400">{formatRelativeTimeET(r.created_at)}</span>
                  </div>

                  <div className="text-xs text-gray-500 mb-1">
                    <span className="text-gray-400">by</span> {r.requested_by_name}
                  </div>

                  {/* The request details — compact */}
                  <div className="text-sm bg-gray-50 rounded px-3 py-2 border-l-3 border-amber-400">
                    <span className="font-medium text-gray-700">{FIELD_LABELS[r.field_name] || r.field_name}:</span>{' '}
                    <span className="text-gray-900">{r.requested_value || r.request_details}</span>
                    {r.reason && (
                      <span className="text-gray-400 ml-2">— {r.reason}</span>
                    )}
                  </div>

                  {/* Rejection reason if rejected */}
                  {r.status === 'rejected' && (r.rejection_reason || r.resolution_notes) && (
                    <div className="mt-1.5 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      Rejected: {r.rejection_reason || r.resolution_notes}
                      {r.handled_by_name && <span className="text-red-400"> — {r.handled_by_name}</span>}
                    </div>
                  )}

                  {r.status === 'approved' && r.handled_by_name && (
                    <div className="mt-1.5 text-xs text-green-600">
                      Approved by {r.handled_by_name}
                    </div>
                  )}
                </div>

                {/* Actions — only for pending */}
                {r.status === 'pending' && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleAction(r.id, 'approved')}
                      disabled={processing === r.id}
                      className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-3"
                      data-testid={`approve-request-${r.id}`}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRejectingId(r.id); setRejectionReason(''); }}
                      disabled={processing === r.id}
                      className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7 px-3"
                      data-testid={`reject-request-${r.id}`}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>

              {/* Inline reject reason input */}
              {rejectingId === r.id && (
                <div className="mt-2 flex gap-2 items-center" data-testid={`reject-form-${r.id}`}>
                  <input
                    type="text"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Reason for rejection (optional)..."
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-red-400"
                    data-testid="rejection-reason-input"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleAction(r.id, 'rejected', rejectionReason)}
                    disabled={processing === r.id}
                    className="text-xs h-7 px-3"
                    data-testid="confirm-reject"
                  >
                    Confirm
                  </Button>
                  <button
                    onClick={() => setRejectingId(null)}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EditRequestsPanel;
